if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', class extends HTMLElement {
    connectedCallback() {
      this.productId = this.dataset.productId;
      this.cartItemIds = (this.dataset.cartItemIds || '').split(',').filter(Boolean);
      this.limit = parseInt(this.dataset.limit) || 4;
      this.listEl = this.querySelector('.cart-upsell__list');

      if (this.productId) {
        this.fetchRecommendations();
      } else {
        // Self-Healing (Bugfix 03.08.): ohne serverseitige productId (Cart war
        // beim Pageload leer) die Basis selbst aus /cart.js ableiten — sonst
        // bleibt der Block nach einem Homepage-Add dauerhaft leer.
        this.refresh();
      }

      document.addEventListener('cart:updated', () => this.refresh());

      // Der Marvya-Kern dispatcht beim Karten-Add KEIN cart:updated — deshalb
      // bei jedem Drawer-Oeffnen den Cart-Stand neu ableiten (Bugfix 03.08.).
      const drawer = this.closest('drawer-component');
      if (drawer) {
        // Nur auf der Flanke zu->offen refreshen — der Observer feuert sonst
        // fuer jede Klassenmutation waehrend der Oeffnungs-Animation mehrfach.
        this._drawerWarOffen = drawer.classList.contains('open');
        new MutationObserver(() => {
          const offen = drawer.classList.contains('open');
          if (offen && !this._drawerWarOffen) this.refresh();
          this._drawerWarOffen = offen;
        }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }
    }

    async fetchRecommendations() {
      try {
        const url = `/recommendations/products.json?product_id=${this.productId}&limit=${this.limit + this.cartItemIds.length}&intent=related`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(res.statusText);
        const data = await res.json();

        const products = (data.products || [])
          .filter(p => !this.cartItemIds.includes(String(p.id)))
          .slice(0, this.limit);

        if (products.length === 0) {
          this.style.display = 'none';
          return;
        }

        this.renderCards(products);
      } catch (e) {
        console.error('Cart upsell error:', e);
        this.style.display = 'none';
      }
    }

    renderCards(products) {
      this.listEl.innerHTML = products.map(product => {
        const variant = product.variants.find(v => v.available) || product.variants[0];
        const image = product.featured_image
          ? product.featured_image.replace(/(\.\w+)$/, '_300x$1')
          : '';
        const comparePrice = variant.compare_at_price && variant.compare_at_price > variant.price
          ? this.formatMoney(variant.compare_at_price)
          : '';
        const price = this.formatMoney(variant.price);

        return `
          <div class="cart-upsell__card" role="listitem">
            <div class="cart-upsell__card-image">
              <a href="${product.url}" tabindex="-1" aria-hidden="true">
                ${image ? `<img src="${image}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="300" height="300">` : ''}
              </a>
              ${variant.available
                ? `<button type="button" class="cart-upsell__add" data-variant-id="${variant.id}" aria-label="${this.escapeHtml(product.title)}">
                    <span class="icon loaded">add</span>
                  </button>`
                : ''
              }
            </div>
            <p class="cart-upsell__card-title">
              <a href="${product.url}">${this.escapeHtml(product.title)}</a>
            </p>
            <p class="cart-upsell__card-price">
              ${comparePrice ? `<s>${comparePrice}</s>` : ''}${price}
            </p>
          </div>
        `;
      }).join('');

      this.listEl.querySelectorAll('.cart-upsell__add[data-variant-id]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleQuickAdd(btn);
        });
      });
    }

    async handleQuickAdd(btn) {
      const variantId = btn.dataset.variantId;
      btn.classList.add('cart-upsell__add--loading');

      try {
        const res = await fetch(routes.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: 1 }] })
        });

        if (!res.ok) throw new Error(res.statusText);

        const cartRes = await fetch('/?section_id=main-cart');
        const html = await cartRes.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        document.querySelectorAll('.cart-items-container').forEach(el => {
          const fresh = doc.querySelector('.cart-items-container');
          if (fresh) el.replaceWith(fresh.cloneNode(true));
        });

        ['.cart-summary__header', '#cart-total-price', '#checkout', '.cart-summary__free-shipping'].forEach(sel => {
          const fresh = doc.querySelector(sel);
          document.querySelectorAll(sel).forEach(el => {
            if (fresh) {
              fresh.querySelectorAll('.icon').forEach(i => i.classList.add('loaded'));
              el.replaceWith(fresh.cloneNode(true));
            }
          });
        });

        const freshCount = doc.querySelector('.cart-count');
        document.querySelectorAll('#cart-open-block .header__icons--cart-open .cart-count').forEach(el => {
          if (freshCount) {
            el.classList.remove('visually-hidden');
            el.textContent = freshCount.textContent;
          }
        });

        if (typeof window.cartManager !== 'undefined') {
          document.querySelectorAll('.cart-item').forEach(item => {
            window.cartManager.manageState(item);
          });
        }

        const miniCart = document.getElementById('mini-cart');
        if (miniCart && typeof cartStrings !== 'undefined' && cartStrings.uxCart === 'mini-cart') {
          miniCart.open();
        }

        document.dispatchEvent(new CustomEvent('cart:updated'));

        this.refresh();
      } catch (e) {
        console.error('Quick add error:', e);
        btn.classList.remove('cart-upsell__add--loading');
      }
    }

    async refresh() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();

        if (cart.item_count === 0) {
          this.style.display = 'none';
          return;
        }

        let highestPrice = 0;
        let baseProductId = cart.items[0].product_id;
        this.cartItemIds = [];

        cart.items.forEach(item => {
          this.cartItemIds.push(String(item.product_id));
          if (item.final_price > highestPrice) {
            highestPrice = item.final_price;
            baseProductId = item.product_id;
          }
        });

        this.productId = baseProductId;
        this.dataset.productId = baseProductId;
        this.dataset.cartItemIds = this.cartItemIds.join(',');

        this.style.display = '';
        await this.fetchRecommendations();
      } catch (e) {
        console.error('Upsell refresh error:', e);
      }
    }

    formatMoney(cents) {
      const amount = cents / 100;
      const currency = (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) || 'EUR';
      const locale = document.documentElement.lang || 'de-DE';
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
      } catch (e) {
        return `${amount.toFixed(2)} ${currency}`;
      }
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  });
}
